import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Award, Crown, Flame, Star, Trophy, TrendingUp, Calendar, CalendarCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { profileService } from "@/services";

const ICON_MAP = { Award, Crown, Flame, Star, Trophy, TrendingUp, Calendar, CalendarCheck };

const TIER_BG = {
  bronze: "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-200",
  silver: "bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-200",
  gold: "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200",
};

// Popover-on-click wrapper. Wrap a player's name anywhere (leaderboard
// row, room player list, in-game scoreboard) and clicking it lazy-
// loads `/profile/{user_id}/public` and renders a compact identity
// card: name, level, streak, win rate, top badges. Avoid wrapping
// when `userId` is missing so we don't fire requests for guest /
// pass-and-play seats.
export default function PlayerNameLink({
  userId,
  name,
  className = "",
  testId,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || data || !userId) return;
    setLoading(true);
    setError(false);
    profileService
      .getPublicProfile(userId)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, userId, data]);

  if (!userId) {
    // No identity to fetch (guest / hot-seat seat). Render the name
    // as plain text so the wrapper is drop-in safe.
    return <span className={className} data-testid={testId}>{name}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`underline decoration-dotted underline-offset-2 hover:text-[#38B6FF] transition-colors ${className}`}
          data-testid={testId || `player-link-${userId}`}
        >
          {name}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        {loading && (
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 text-center py-2">
            {t("common.loading")}
          </p>
        )}
        {error && (
          <p className="text-xs text-[#FF5A5F] text-center py-2">
            {t("playerLink.error")}
          </p>
        )}
        {data && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] truncate" style={{ fontFamily: "Fredoka" }}>
                {data.name}
              </p>
              <span className="text-[10px] font-semibold text-[#1A237E]/60 dark:text-[#E3F2FD]/60 shrink-0">
                {t("playerLink.level", { level: data.level, name: data.level_name })}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-lg p-1.5">
                <p className="text-[10px] text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("playerLink.wins")}</p>
                <p className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD]">{data.wins}</p>
              </div>
              <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-lg p-1.5">
                <p className="text-[10px] text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("playerLink.winRate")}</p>
                <p className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD]">
                  {data.games_played ? `${Math.round((data.win_rate || 0) * 100)}%` : "-"}
                </p>
              </div>
              <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-lg p-1.5">
                <p className="text-[10px] text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("playerLink.streak")}</p>
                <p className="text-sm font-bold text-[#FF5A5F]">{data.current_streak}</p>
              </div>
            </div>
            {(data.badges || []).length > 0 ? (
              <div className="border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 pt-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mb-1.5">
                  {t("playerLink.badges", { count: data.badges.length })}
                </p>
                <div className="flex flex-wrap gap-1">
                  {/* Show top 6 — ordered by tier (gold → silver → bronze). */}
                  {[...data.badges]
                    .sort((a, b) => {
                      const order = { gold: 0, silver: 1, bronze: 2 };
                      return (order[a.tier] ?? 9) - (order[b.tier] ?? 9);
                    })
                    .slice(0, 6)
                    .map((b) => {
                      const Icon = ICON_MAP[b.icon] || Award;
                      return (
                        <Badge
                          key={b.id}
                          className={`${TIER_BG[b.tier] || TIER_BG.bronze} text-[10px] gap-1`}
                          title={b.description}
                        >
                          <Icon className="w-3 h-3" />
                          {b.label}
                        </Badge>
                      );
                    })}
                  {data.badges.length > 6 && (
                    <Badge className="text-[10px] bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
                      {t("playerLink.moreBadges", { count: data.badges.length - 6 })}
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-[10px] text-[#1A237E]/40 dark:text-[#E3F2FD]/40 text-center pt-2 border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10">
                {t("playerLink.noBadges")}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
